import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Typography, CircularProgress, Paper, Box, TextField } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import ReactFlow, { Controls, Background, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import Dagre from "@dagrejs/dagre";

function ShowWorkflow() {
  const { id } = useParams();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  useEffect(() => {
        const fetchWorkflow = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/workflows/workflow/${id}`);
            setWorkflow(response.data);
        } catch (err) {
            setError(err.message);
            enqueueSnackbar("Failed to load workflow preview", { variant: "error" });
        } finally {
            setLoading(false);
        }
        };

        fetchWorkflow();
    }, [id]);

    const getLayoutedElements = (nodes, edges) => {
        const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
        g.setGraph({ rankdir: "TB", nodesep: 100, ranksep: 100, marginx: 20, marginy: 20 });

        edges.forEach((edge) => g.setEdge(edge.source, edge.target));
        nodes.forEach((node) => g.setNode(node.id, { width: 120, height: 60 }));

        Dagre.layout(g);

        return {
            nodes: nodes.map((node) => ({
                ...node,
                position: { x: g.node(node.id).x, y: g.node(node.id).y },
            })),
            edges,
        };
    };

    useEffect(() => {
        if (workflow) {
            const nodeData = workflow.nodes.map((node) => ({
                id: node.id,
                data: { label: node.name },
                position: { x: 0, y: 0 },
            }));

            const edgeData = workflow.nodes.flatMap((node) =>
                node.outgoingEdges.map((targetId) => ({
                id: `${node.id}-${targetId}`,
                source: node.id,
                target: targetId,
                animated: true,
                }))
            );

            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodeData, edgeData);

            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        }
    }, [workflow, setNodes, setEdges]);

    if (loading) return <CircularProgress />;
    if (error)
        return (
            <Typography color="error" variant="body1">
                {error}
            </Typography>
        );

  return (
    <Box sx={{ padding: 3, width: "90%", margin: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary" align="center">
            {workflow.title}
        </Typography>

        <Paper sx={{ padding: 3, width: "100%", minHeight: "auto" }}>
            <Typography variant="h5" color="text.primary" sx={{ marginBottom: 2 }}>
                Workflow Graph
            </Typography>
            <Box sx={{ width: "100%", height: "500px", backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                <ReactFlow 
                    nodes={nodes}
                    edges={edges} 
                    fitView 
                    proOptions={{ hideAttribution: true }} 
                    style={{ width: "100%", height: "100%" }}
                >
                    <Controls />
                    <Background />
                </ReactFlow>
            </Box>
        </Paper>
    </Box>
  );
}

export default ShowWorkflow;
